import { FlexRow } from '@/components/common/Flex';
import { Circle, FlexProps, Grid, GridProps } from '@chakra-ui/react';
import { Goal } from '@common/goals';
import {
    getAmountRecurrenceStr,
    getPartnerGoalAccount,
} from '@common/react/goals';
import { useCurrentRateInfo } from '@common/react/rates';
import { getPartnerName, useCurrentUser } from '@common/react/user';
import styleguide from '@common/styleguide';
import { TransactionSchedulePlan } from '@common/types';
import { TickRange } from '@common/valueSeries';
import { ParentSize } from '@visx/responsive';
import ErrorBoundary from '../common/ErrorBoundary';
import FadeInBox from '../common/FadeInBox';
import InputField from '../common/InputField';
import TransactionScheduleInput from '../common/TransactionScheduleInput';
import { Text } from '../common/Typography';
import { configByGoalType } from './config';
import GoalsChart, { GoalsChartEntityParams } from './GoalsChart';
import GoalTimingStatement from './GoalTimingStatement';
import {
    getGoalDataFromExistingGoal,
    getGoalTransactionScheduleInputSliderConfig,
    useInitialDepositPlanSliderConfig,
} from './helpers';

// TODO: Remove/relocate after Retirement goal update

export interface GoalOnboardingPlanInputsAndChartProps {
    goal: Goal;
    initialDepositPlan: number;
    onChangeInitialDepositPlan: (initialDepositPlan: number) => void;
    transactionSchedulePlan: TransactionSchedulePlan;
    onChangeTransactionSchedulePlan: (
        transactionSchedulePlan: TransactionSchedulePlan
    ) => void;
    partnerTransactionSchedulePlan: TransactionSchedulePlan | undefined;
    forecastCompletionDate: string | undefined;
    chartEntities: GoalsChartEntityParams[];
    range: TickRange;
}

const GoalOnboardingPlanInputsAndChart = ({
    goal,
    initialDepositPlan,
    onChangeInitialDepositPlan,
    transactionSchedulePlan,
    onChangeTransactionSchedulePlan,
    partnerTransactionSchedulePlan,
    forecastCompletionDate,
    chartEntities,
    range,
}: GoalOnboardingPlanInputsAndChartProps) => {
    const { userInfo } = useCurrentUser();
    const { currentRateInfo } = useCurrentRateInfo();
    const goalConfig = configByGoalType[goal.type];
    const goalData = getGoalDataFromExistingGoal(goal);
    const targetDate = currentRateInfo
        ? goalConfig.getTargetDate({
              goalData,
              userInfo,
              currentRateInfo,
          })
        : undefined;

    return (
        <FadeInBox h="full">
            <InputsContainer
                goal={goal}
                initialDepositPlan={initialDepositPlan}
                onChangeInitialDepositPlan={onChangeInitialDepositPlan}
                transactionSchedulePlan={transactionSchedulePlan}
                onChangeTransactionSchedulePlan={
                    onChangeTransactionSchedulePlan
                }
                partnerTransactionSchedulePlan={partnerTransactionSchedulePlan}
                mb={8}
            />
            {targetDate && (
                <GoalTimingStatement
                    targetDate={targetDate}
                    forecastCompletionDate={forecastCompletionDate}
                    mb={8}
                />
            )}
            <ChartContainer chartEntities={chartEntities} range={range} />
        </FadeInBox>
    );
};

interface InputsContainerProps extends GridProps {
    goal: Goal;
    initialDepositPlan: number;
    onChangeInitialDepositPlan: (initialDepositPlan: number) => void;
    transactionSchedulePlan: TransactionSchedulePlan;
    onChangeTransactionSchedulePlan: (
        transactionSchedulePlan: TransactionSchedulePlan
    ) => void;
    partnerTransactionSchedulePlan?: TransactionSchedulePlan;
}

const InputsContainer = ({
    goal,
    initialDepositPlan,
    onChangeInitialDepositPlan,
    transactionSchedulePlan,
    onChangeTransactionSchedulePlan,
    partnerTransactionSchedulePlan,
    ...props
}: InputsContainerProps) => {
    const { partnerInfo } = useCurrentUser();
    const isSharedGoal = goal.shareStatus === 'SHARED';
    const partnerGoalAccount = getPartnerGoalAccount(goal);
    const partnerName = getPartnerName(partnerInfo);
    const initialDepositPlanSliderConfig = useInitialDepositPlanSliderConfig({
        initialDepositPlan,
        targetAmount: goal.targetAmount,
    });
    const transactionSchedulePlanSliderConfig =
        getGoalTransactionScheduleInputSliderConfig({
            targetAmount: goal.targetAmount,
            startAmount: initialDepositPlan,
        });
    const goalConfig = configByGoalType[goal.type];
    const fundingType =
        goalConfig.group === 'HIGH_YIELD_CASH_ONLY'
            ? 'contribution'
            : 'investment';

    return (
        <Grid templateColumns="130px 10px 1.5fr 1fr" gap={10} {...props}>
            <InputField
                name="initialDepositPlan"
                label={`One time ${fundingType}`}
                labelProps={{ whiteSpace: 'nowrap' }}
                type="currency"
                value={initialDepositPlan}
                onChange={onChangeInitialDepositPlan}
                inputProps={{ variant: 'flushed' }}
                inputOptions={{ min: 0 }}
                slider={initialDepositPlanSliderConfig}
            />
            <Text mt={8}>+</Text>
            <TransactionScheduleInput
                datePickerLabel={
                    isSharedGoal ? (
                        <FlexRow alignItems="center" gap={2}>
                            <Circle
                                size={1.5}
                                bg={styleguide.colors.blueBright}
                            />
                            {`My ${fundingType} starting`}
                        </FlexRow>
                    ) : (
                        `Repeating ${fundingType} starting`
                    )
                }
                transactionSchedule={transactionSchedulePlan}
                onChangeTransactionSchedule={onChangeTransactionSchedulePlan}
                slider={transactionSchedulePlanSliderConfig}
                showDatePicker
                datePickerValue={transactionSchedulePlan.startDate}
            />
            {partnerTransactionSchedulePlan && (
                <InputField
                    name="partnerTransactionSchedule"
                    label={
                        <FlexRow
                            alignItems="center"
                            gap={2}
                            whiteSpace="nowrap"
                        >
                            <Circle
                                size={1.5}
                                bg={styleguide.colors.orangeBright}
                            />
                            {partnerGoalAccount
                                ? `${partnerName} is already ${
                                      goalConfig.group ===
                                      'HIGH_YIELD_CASH_ONLY'
                                          ? 'contributing'
                                          : 'investing'
                                  }`
                                : `${partnerName}'s suggested ${fundingType}`}
                        </FlexRow>
                    }
                    type="text"
                    value={getAmountRecurrenceStr(
                        partnerTransactionSchedulePlan
                    )}
                    onChange={() => undefined}
                    isReadOnly
                />
            )}
        </Grid>
    );
};

interface ChartContainerProps extends FlexProps {
    chartEntities: GoalsChartEntityParams[];
    range: TickRange;
}

const ChartContainer = ({
    chartEntities,
    range,
    ...props
}: ChartContainerProps) => {
    return (
        <FlexRow grow={1} mb={8} minH="3xs" position="relative" {...props}>
            <FlexRow
                position="absolute"
                h="full"
                w="full"
                align="center"
                justify="center"
            >
                <ParentSize>
                    {(parent) => (
                        <ErrorBoundary>
                            <GoalsChart
                                width={parent.width}
                                height={parent.height}
                                range={range}
                                entities={chartEntities}
                            />
                        </ErrorBoundary>
                    )}
                </ParentSize>
            </FlexRow>
        </FlexRow>
    );
};

export default GoalOnboardingPlanInputsAndChart;
